import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';
import type { DealroomStatus } from './types.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

/** While a job is queued or running the page asks again every five seconds. */
const POLL_MS = 5_000;

export interface Dealroom {
  status: DealroomStatus | undefined;
  /** Matches the list against Dealroom, after a confirm that says what it spends. */
  startMatch: () => void;
  isStarting: boolean;
}

function isBusy(status: DealroomStatus | undefined): boolean {
  return (status?.lastRunByKind ?? []).some(
    (run) => run.status === 'queued' || run.status === 'running',
  );
}

export function useDealroom(): Dealroom {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();

  const status = useQuery(
    trpc.dealroom.status.queryOptions(undefined, {
      refetchInterval: (query) => (isBusy(query.state.data) ? POLL_MS : false),
    }),
  );

  const match = useMutation(
    trpc.dealroom.match.mutationOptions({
      onSuccess: () => {
        toast('Matching the funds against Dealroom…', { tone: 'info' });
        void queryClient.invalidateQueries({ queryKey: trpc.dealroom.status.queryKey() });
      },
      onError: (error) => toast(error.message),
    }),
  );

  // A finished job has written funds, rounds and candidates, so everything the
  // pages are holding is stale the moment it ends rather than when it starts.
  const lastFinishedAt = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (status.data === undefined) return;
    // The most recent finish across the kinds: any of them writes rows the
    // pages are holding.
    const finishedAt =
      [...(status.data?.lastRunByKind ?? [])]
        .map((run) => run.finishedAt)
        .filter((at): at is string => at !== null)
        .sort()
        .pop() ?? null;
    // The first answer is a run that ended before this page opened, which the
    // rows on it already show: refetching for it would load the grid twice.
    const first = lastFinishedAt.current === undefined;
    if (finishedAt === lastFinishedAt.current) return;
    lastFinishedAt.current = finishedAt;
    if (first || finishedAt === null) return;
    invalidateFundRows(queryClient, trpc);
    // `pathKey` rather than `queryKey`: the rounds table pages, so its cache
    // entry is the infinite one.
    void queryClient.invalidateQueries({ queryKey: trpc.rounds.list.pathKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.candidates.list.queryKey() });
  }, [status.data, queryClient, trpc]);

  const startMatch = useCallback(() => {
    const ceiling = status.data?.ceilingPerDay ?? 0;
    const spent = status.data?.callsToday ?? 0;
    if (
      !window.confirm(
        `Match every fund against Dealroom? ${spent} of today’s ${ceiling} calls are spent.`,
      )
    ) {
      return;
    }
    match.mutate({});
  }, [match, status.data]);

  return { status: status.data, startMatch, isStarting: match.isPending };
}
